import { useRef, useState, useEffect, useCallback } from 'react';

// Card aspect ratio (credit card)
const CARD_ASPECT_RATIO = 2.125 / 3.37; // width / height = ~0.63

// Visual bleed percentage for UI (exaggerated for visibility)
// Actual print bleed is 0.2mm per edge
const BLEED_VISUAL_PERCENT = 0.03; // 3% per edge

interface ImageCropperProps {
  imageUrl: string;
  onCropComplete: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

export default function ImageCropper({ imageUrl, onCropComplete, onCancel }: ImageCropperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Crop state
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropScale, setCropScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Calculate crop frame dimensions
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth - 40; // padding
    const containerHeight = containerRef.current.clientHeight - 120; // buttons + padding

    // Fit frame within container maintaining card aspect ratio
    let frameWidth = containerWidth;
    let frameHeight = frameWidth / CARD_ASPECT_RATIO;

    if (frameHeight > containerHeight) {
      frameHeight = containerHeight;
      frameWidth = frameHeight * CARD_ASPECT_RATIO;
    }

    setFrameSize({ width: frameWidth, height: frameHeight });
  }, []);

  // Load image and get dimensions
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      setImageLoaded(true);

      // Initialize scale to fit image within frame
      const scaleX = frameSize.width / img.width;
      const scaleY = frameSize.height / img.height;
      const initialScale = Math.max(scaleX, scaleY) * 1.1; // Slightly larger to allow adjustment
      setCropScale(initialScale);
    };
    img.src = imageUrl;
  }, [imageUrl, frameSize]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: cropOffset.x,
      offsetY: cropOffset.y,
    };
  }, [cropOffset]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;

    setCropOffset({
      x: dragStart.current.offsetX + deltaX,
      y: dragStart.current.offsetY + deltaY,
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.95 : 1.05;
    setCropScale(prev => Math.max(0.1, Math.min(5, prev * delta)));
  }, []);

  const handleApply = useCallback(() => {
    if (!imageRef.current || !frameSize.width) return;

    // Calculate the crop area in image coordinates
    const scaledWidth = imageDimensions.width * cropScale;
    const scaledHeight = imageDimensions.height * cropScale;

    // Frame center position relative to scaled image
    const frameCenterX = scaledWidth / 2 - cropOffset.x;
    const frameCenterY = scaledHeight / 2 - cropOffset.y;

    // Convert to image coordinates
    const cropX = (frameCenterX - frameSize.width / 2) / cropScale;
    const cropY = (frameCenterY - frameSize.height / 2) / cropScale;
    const cropWidth = frameSize.width / cropScale;
    const cropHeight = frameSize.height / cropScale;

    // Create canvas for cropping
    const canvas = document.createElement('canvas');
    const outputWidth = 1024; // Fixed output width for quality
    const outputHeight = outputWidth / CARD_ASPECT_RATIO;
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw cropped region
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(
        img,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, outputWidth, outputHeight
      );
      const croppedDataUrl = canvas.toDataURL('image/png');
      onCropComplete(croppedDataUrl);
    };
    img.src = imageUrl;
  }, [imageUrl, imageDimensions, cropOffset, cropScale, frameSize, onCropComplete]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        ref={containerRef}
        style={{
          flex: 1,
          width: '100%',
          maxWidth: '800px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Crop area */}
        <div
          style={{
            position: 'relative',
            width: frameSize.width,
            height: frameSize.height,
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          {/* Image */}
          {imageLoaded && (
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Crop preview"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) translate(${cropOffset.x}px, ${cropOffset.y}px)`,
                width: imageDimensions.width * cropScale,
                height: imageDimensions.height * cropScale,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              draggable={false}
            />
          )}

          {/* Bleed overlay - outer edge */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: `${frameSize.width * BLEED_VISUAL_PERCENT}px solid rgba(255, 100, 100, 0.3)`,
              pointerEvents: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Safe area outline */}
          <div
            style={{
              position: 'absolute',
              top: frameSize.width * BLEED_VISUAL_PERCENT,
              left: frameSize.width * BLEED_VISUAL_PERCENT,
              right: frameSize.width * BLEED_VISUAL_PERCENT,
              bottom: frameSize.width * BLEED_VISUAL_PERCENT,
              border: '2px dashed rgba(255, 255, 255, 0.5)',
              pointerEvents: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Corner guides */}
          {[0, 1, 2, 3].map((corner) => {
            const isTop = corner < 2;
            const isLeft = corner % 2 === 0;
            return (
              <div
                key={corner}
                style={{
                  position: 'absolute',
                  [isTop ? 'top' : 'bottom']: 0,
                  [isLeft ? 'left' : 'right']: 0,
                  width: '20px',
                  height: '20px',
                  borderColor: 'white',
                  borderStyle: 'solid',
                  borderWidth: 0,
                  [isTop ? 'borderTopWidth' : 'borderBottomWidth']: '3px',
                  [isLeft ? 'borderLeftWidth' : 'borderRightWidth']: '3px',
                  pointerEvents: 'none',
                }}
              />
            );
          })}
        </div>

        {/* Instructions */}
        <p style={{ color: '#888888', fontSize: '14px', marginTop: '16px', textAlign: 'center' }}>
          Drag to reposition. Scroll to zoom. Red edge may be trimmed during printing.
        </p>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: 'transparent',
            border: '1px solid #666666',
            borderRadius: '8px',
            color: '#ffffff',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            color: '#000000',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
